import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-hot-toast'
import Rating from '@mui/material/Rating'
import { useAudioRecorder } from 'react-audio-voice-recorder'
import { FiSend, FiMic, FiSquare, FiTrash2, FiLock, FiCheckCircle, FiStar, FiMessageSquare, FiPlay, FiBookOpen, FiBriefcase, FiHeart, FiChevronRight } from 'react-icons/fi'
import { HiAcademicCap } from 'react-icons/hi2'
import { SubmitFeedbackService } from '../../services/TeacherFeedbackServices'
import { Hero, SectionLabel, Card, PrimaryButton, BRAND_NAVY, BRAND_GOLD, BG_SOFT } from './components/PublicHeader'

const QUESTIONS = [
  { key: 'teaching_effectiveness', label: "How satisfied are you with the teacher's teaching quality?" },
  { key: 'student_improvement', label: 'Does your child understand concepts clearly after attending classes?' },
  { key: 'classroom_engagement', label: 'Does the teacher give sufficient attention to students?' },
  { key: 'discipline_accountability', label: "How satisfied are you with your child's academic progress?" },
  {
    key: 'overall_satisfaction',
    label: 'Would you like your child to continue learning from this teacher in the future?',
    type: 'choice'
  }
]

// overall_satisfaction is still stored as a 1-5 number on the backend (same
// schema/aggregation as every other question), so the 3 choices map onto
// that scale instead of needing a schema change.
const CONTINUE_OPTIONS = [
  { label: 'Definitely Yes', value: 5 },
  { label: 'Maybe', value: 3 },
  { label: 'No', value: 1 }
]

const MAX_RECORD_SECONDS = 60

const CONFETTI = [
  { top: '-4%', left: '10%', color: '#f5a623' },
  { top: '4%', right: '8%', color: '#3b6fd9' },
  { top: '22%', left: '4%', color: '#2e9e63' },
  { top: '10%', right: '20%', color: '#d1497a' }
]

// The mockup's hero icon is a speech-bubble with a star inside it — react-icons
// doesn't have that exact glyph, so composite it from FiMessageSquare + FiStar.
function ChatStarIcon({ size = 24, style }) {
  return (
    <span className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
      <FiMessageSquare size={size} style={style} />
      <FiStar size={size * 0.4} className="absolute" style={{ color: BRAND_GOLD, fill: BRAND_GOLD, top: '26%' }} />
    </span>
  )
}

export default function PublicFeedbackForm() {
  const navigate = useNavigate()
  const { teacherId } = useParams()

  const [student, setStudent] = useState(null)
  const [teacher, setTeacher] = useState(null)
  const [existing, setExisting] = useState(null)
  const [ratings, setRatings] = useState({})
  const [feedbackText, setFeedbackText] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const recorder = useAudioRecorder()

  useEffect(() => {
    const rawStudent = sessionStorage.getItem('feedback_student')
    const rawTeacher = sessionStorage.getItem('feedback_teacher')
    const rawExisting = sessionStorage.getItem('feedback_existing')

    if (!rawStudent || !rawTeacher) {
      navigate('/parent_feedback')
      return
    }

    setStudent(JSON.parse(rawStudent))
    setTeacher(JSON.parse(rawTeacher))
    if (rawExisting) setExisting(JSON.parse(rawExisting))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teacherId])

  if (recorder.isRecording && recorder.recordingTime >= MAX_RECORD_SECONDS) {
    recorder.stopRecording()
  }

  async function handleSubmit() {
    const missing = QUESTIONS.find(q => !ratings[q.key])
    if (missing) {
      toast.error('Please rate every question before submitting')
      document.getElementById(`q-${missing.key}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      return
    }

    setSubmitting(true)
    const formData = new FormData()
    formData.append('student', student.studentId)
    formData.append('teacher', teacherId)
    formData.append('feedback_text', feedbackText)
    Object.keys(ratings).forEach(key => formData.append(key, ratings[key]))
    if (recorder.recordingBlob) {
      formData.append('voice', recorder.recordingBlob, 'voice_feedback.webm')
    }

    const result = await SubmitFeedbackService(formData)
    setSubmitting(false)

    if (result?.data?.status) {
      toast.success('Thank you! Your feedback has been submitted.')
      sessionStorage.setItem('feedback_existing', JSON.stringify(result.data.data))
      setExisting(result.data.data)
    } else {
      toast.error(result?.data?.message || 'Could not submit feedback. Please try again.')
    }
  }

  if (!student || !teacher) return null

  return (
    <div className="min-h-screen bg-white">
      <Hero
        icon={ChatStarIcon}
        title={existing ? 'Feedback Submitted!' : 'Teacher Feedback'}
        subtitle={existing
          ? "Thank you for helping us improve your child's learning experience."
          : "Your honest feedback helps us improve the learning experience for your child and other students."}
        onBack={() => navigate('/parent_feedback/teachers')}
      />

      <div className="max-w-lg mx-auto px-4 -mt-7 pb-10 relative">
        {existing ? (
          <Card className="text-center relative overflow-hidden">
            {CONFETTI.map((c, i) => (
              <span key={i} className="absolute w-2 h-2 rounded-full opacity-70" style={{ top: c.top, left: c.left, right: c.right, background: c.color }} />
            ))}

            <div
              className="mx-auto w-20 h-20 rounded-full bg-white flex items-center justify-center mb-4"
              style={{ boxShadow: '0 8px 24px -6px rgba(26,157,92,0.35), 0 0 0 6px #eafaf0' }}
            >
              <FiCheckCircle className="text-[#1a9d5c]" size={38} />
            </div>
            <p className="font-extrabold text-gray-900 text-[20px]">Thank You!</p>
            <p className="text-[13.5px] text-gray-500 mt-2 leading-relaxed max-w-[280px] mx-auto">
              Your feedback for <span className="font-bold" style={{ color: BRAND_NAVY }}>{teacher.name}</span> has been submitted successfully.
            </p>

            <div className="mx-auto w-11 h-11 rounded-full flex items-center justify-center mt-5 mb-3" style={{ background: '#fdeaec' }}>
              <FiHeart className="text-[#e0658a]" size={18} />
            </div>
            <p className="text-[12px] text-gray-400 max-w-[220px] mx-auto leading-relaxed mb-6">
              Your opinion helps us improve the learning experience for students.
            </p>

            <PrimaryButton onClick={() => navigate('/parent_feedback/teachers')}>
              Review Another Teacher <FiChevronRight />
            </PrimaryButton>
          </Card>
        ) : (
          <>
          <Card className="mb-4 flex items-center gap-3.5">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center text-xl font-bold text-gray-400 overflow-hidden flex-shrink-0">
              {teacher.profile_photo
                ? <img src={`${process.env.REACT_APP_AWS_IMAGE_URL || ''}${teacher.profile_photo}`} alt={teacher.name} className="w-full h-full object-cover" />
                : teacher.name?.[0]}
            </div>
            <div className="min-w-0">
              <p className="font-extrabold text-gray-900 text-[16px]">{teacher.name}</p>
              {teacher?.expertise && (
                <span className="inline-block text-[10.5px] font-bold px-2.5 py-0.5 rounded-full mt-1" style={{ background: '#e8f0fe', color: '#3b6fd9' }}>
                  {teacher.expertise}
                </span>
              )}
              <div className="mt-1.5 space-y-0.5">
                {teacher.subjects?.length > 0 && (
                  <p className="text-[11px] text-gray-500 flex items-center gap-1.5"><FiBookOpen size={11} /> Subjects: {teacher.subjects.join(', ')}</p>
                )}
                {teacher.qualification && (
                  <p className="text-[11px] text-gray-500 flex items-center gap-1.5"><HiAcademicCap size={12} /> Qualification: {teacher.qualification}</p>
                )}
                {teacher.experience && (
                  <p className="text-[11px] text-gray-500 flex items-center gap-1.5"><FiBriefcase size={11} /> Experience: {teacher.experience}</p>
                )}
              </div>
            </div>
          </Card>

          <Card>
            <div className="pb-3 border-b border-gray-100">
              <SectionLabel icon={FiStar} title="Please rate this teacher" />
            </div>

            <div className="divide-y divide-gray-100">
              {QUESTIONS.map((q, i) => {
                const value = ratings[q.key] || 0
                return (
                  <div key={q.key} id={`q-${q.key}`} className="flex items-start gap-3 py-4 first:pt-4">
                    <span
                      className="w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold flex-shrink-0 mt-0.5 text-white"
                      style={{ background: BRAND_NAVY }}
                    >
                      {i + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-[12px] font-medium text-gray-600 leading-snug mb-2">{q.label}</p>
                      {q.type === 'choice' ? (
                        <div className="flex items-center gap-2 flex-wrap">
                          {CONTINUE_OPTIONS.map(opt => (
                            <button
                              key={opt.value}
                              type="button"
                              onClick={() => setRatings(prev => ({ ...prev, [q.key]: opt.value }))}
                              className="px-3.5 py-2 rounded-xl text-[12.5px] font-bold border-2 transition-colors active:scale-95"
                              style={value === opt.value
                                ? { background: BRAND_NAVY, borderColor: BRAND_NAVY, color: 'white' }
                                : { background: 'white', borderColor: '#e5e7eb', color: '#4b5563' }}
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      ) : (
                        <div className="flex items-center justify-between">
                          <Rating
                            value={value}
                            onChange={(e, val) => setRatings(prev => ({ ...prev, [q.key]: val }))}
                            sx={{ color: BRAND_GOLD, fontSize: '1.7rem' }}
                          />
                          <div className="flex justify-between text-[9.5px] text-gray-300 font-medium w-[74px] flex-shrink-0">
                            <span>Poor</span><span>Excellent</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>

            <div className="mt-6 pt-5 border-t border-gray-100">
              <SectionLabel icon={FiMessageSquare} title="Additional Feedback (Optional)" subtitle="Share your suggestions or comments" className="mb-3" />
              <textarea
                value={feedbackText}
                onChange={(e) => setFeedbackText(e.target.value.slice(0, 500))}
                rows={3}
                placeholder="Write your feedback here…"
                className="w-full border-2 border-gray-200 rounded-xl px-3.5 py-3 text-[13.5px] focus:outline-none focus:border-[#14213d] transition-colors resize-none"
              />
              <p className="text-[11px] text-gray-300 text-right mt-1">{feedbackText.length}/500</p>
            </div>

            <div className="mt-5 pt-5 border-t border-gray-100">
              <SectionLabel icon={FiMic} title="Voice Feedback (Optional)" subtitle="Record your voice message (Max 60 seconds)" className="mb-3" />

              <div
                className="flex items-center justify-between rounded-xl px-4 py-3.5 border-2 transition-colors"
                style={{ background: recorder.isRecording ? '#fef2f2' : BG_SOFT, borderColor: recorder.isRecording ? '#fecaca' : 'transparent' }}
              >
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={recorder.isRecording ? recorder.stopRecording : recorder.startRecording}
                    className={`w-10 h-10 rounded-full flex items-center justify-center text-white flex-shrink-0 shadow-md border-4 border-white ring-1 ring-gray-200 ${recorder.isRecording ? 'animate-pulse' : ''}`}
                    style={{ background: '#e5484d' }}
                  >
                    {recorder.isRecording ? <FiSquare size={12} /> : <span className="w-3 h-3 rounded-full bg-white block" />}
                  </button>
                  <div>
                    <span className="text-[13px] font-semibold text-gray-700 block leading-tight">
                      {recorder.isRecording ? 'Recording…' : recorder.recordingBlob ? 'Voice note recorded' : 'Tap to Record'}
                    </span>
                    <span className="text-[11px] text-gray-400">
                      {String(Math.min(recorder.recordingTime, MAX_RECORD_SECONDS)).padStart(2, '0')}:00 / 01:00
                    </span>
                  </div>
                </div>
                {recorder.recordingBlob && (
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <PlayButton blob={recorder.recordingBlob} />
                    <button type="button" onClick={() => window.location.reload()} className="text-red-400 hover:text-red-500 flex items-center gap-1 text-[11px] font-bold">
                      <FiTrash2 size={13} /> Delete
                    </button>
                  </div>
                )}
              </div>
              <p className="text-[10.5px] text-gray-300 mt-2">You can re-record if you want to change the voice message.</p>
            </div>

            <div className="mt-6 rounded-xl px-4 py-3 flex items-center gap-3" style={{ background: BG_SOFT }}>
              <span className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: '#e5e9f5' }}>
                <FiLock style={{ color: BRAND_NAVY }} size={13} />
              </span>
              <p className="text-[9.5px] text-gray-500 leading-snug">
                Your feedback is <span className="font-bold" style={{ color: BRAND_NAVY }}>confidential</span> and will be <span className="font-bold" style={{ color: BRAND_NAVY }}>used only for improvement purposes</span>.
              </p>
            </div>

            <div className="mt-5">
              <PrimaryButton onClick={handleSubmit} loading={submitting}>
                {submitting ? 'Submitting…' : <><FiSend size={14} /> Submit Feedback</>}
              </PrimaryButton>
            </div>
          </Card>
          </>
        )}

        {!existing && <p className="text-center text-gray-400 text-[12px] mt-6">Thank you for taking the time to share your feedback! 💛</p>}
      </div>
    </div>
  )
}

function PlayButton({ blob }) {
  const [playing, setPlaying] = useState(false)
  const url = React.useMemo(() => URL.createObjectURL(blob), [blob])
  return (
    <>
      <button
        type="button"
        onClick={() => setPlaying(v => !v)}
        className="flex items-center gap-1.5"
      >
        <span className="w-7 h-7 rounded-full flex items-center justify-center text-white flex-shrink-0" style={{ background: BRAND_NAVY }}>
          <FiPlay size={11} />
        </span>
        <span className="text-[11px] font-bold" style={{ color: BRAND_NAVY }}>Play</span>
      </button>
      {playing && <audio autoPlay src={url} onEnded={() => setPlaying(false)} className="hidden" />}
    </>
  )
}
